/* eslint-disable react/jsx-no-target-blank */
import React, { useContext, useState, useEffect } from 'react'
import { GatsbyImage } from 'gatsby-plugin-image'
import PropTypes from 'prop-types'
import TransitionLink from 'gatsby-plugin-transition-link'

// Functions
import { externalLinkCheck, countryCookieCheck } from '../../utils/utils'

// Components
import { StoreDispatch, StoreState } from '../../Store'
import Spacer from '../Spacer'
import AnimateSplitText from '../animation/AnimateSplitText'
import AnimateScaleIn from '../animation/AnimateScaleIn'
import AnimateSlideText from '../animation/AnimateSlideText'

// Vars
import { Title, Label } from '../TextStyles'

// SVGs
import Pin from '../svgs/Pin'
import ViewMore from '../svgs/ViewMore'

// Styles
import {
  CarouselItemMain,
  CarouselItemImage,
  CarouselItemImageInner,
  CarouselItemImageHover,
  CarouselItemText,
  CarouselItemTitle,
  CarouselItemLabel,
  CarouselItemPin,
} from './index.style'
import { animation } from '../../styles/vars/animation.style'

/*
 ** Conditional wrapper component to reduce duplicate code
 ** Wraps if condition is met, otherwise returns children without wrapper
 */
const ConditionalWrapper = ({ condition, wrapper, children }) => {
  return condition ? wrapper(children) : children
}

const CarouselItem = ({
  id,
  image,
  featured,
  title,
  label,
  index,
  transitions,
  event,
  link,
}) => {
  const dispatch = useContext(StoreDispatch)
  const store = useContext(StoreState)
  const [hovered, setHovered] = useState(false)
  const [externalUrl, setExternalUrl] = useState(
    link?.externalLink ? link.externalLink : null
  )

  useEffect(() => {
    const linkSuffix = countryCookieCheck(store.countryCookie)

    if (externalUrl !== null) {
      const updatedUrl = externalLinkCheck(externalUrl, linkSuffix)
      setExternalUrl(updatedUrl)
    }
  }, [store.countryCookie, externalUrl])

  return (
    <CarouselItemMain
      featured={featured}
      index={index}
      transitions={transitions}
      onClick={event}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <ConditionalWrapper
        condition={link && !featured ? true : false}
        wrapper={children => {
          return link?.externalLink ? (
            <a
              href={externalUrl}
              target={'_blank'}
              rel={
                link.externalLink.includes('royalcaribbean.com')
                  ? ``
                  : `noreferrer`
              }
            >
              {children}
            </a>
          ) : (
            <TransitionLink
              to={
                link.internalLink.internal.type === 'ContentfulPageItinerary'
                  ? `/itinerary`
                  : `/`
              }
              exit={{
                delay: animation.maskShowDuration,
                length: 0,
                zIndex: 2,
              }}
              entry={{
                delay: 0,
                length: animation.maskHideDuration,
                zIndex: 1,
              }}
              onClick={() => {
                dispatch({
                  type: 'UPDATE_PATHNAME',
                  payload:
                    link.internalLink.internal.type ===
                    'ContentfulPageItinerary'
                      ? `/itinerary`
                      : `/`,
                })
                dispatch({ type: 'SHOW_TRANSITION_MASK' })
              }}
              aria-label={title}
            >
              {children}
            </TransitionLink>
          )
        }}
      >
        <React.Fragment>
          <CarouselItemImage
            featured={featured}
            index={index}
            transitions={transitions}
          >
            <CarouselItemImageInner
              featured={featured}
              index={index}
              transitions={transitions}
            >
              <AnimateScaleIn id={id}>
                <CarouselItemImageHover>
                  <GatsbyImage
                    image={image.gatsbyImageData}
                    alt={title}
                    width={1722}
                    height={1150}
                  />
                  <ViewMore active={hovered} />
                </CarouselItemImageHover>
              </AnimateScaleIn>
            </CarouselItemImageInner>
          </CarouselItemImage>

          <CarouselItemText
            featured={featured}
            index={index}
            transitions={transitions}
          >
            <Spacer size={22} />
            <CarouselItemTitle>
              <Title>
                <AnimateSplitText id={id + `-title`}>{title}</AnimateSplitText>
              </Title>
            </CarouselItemTitle>
            {label && (
              <>
                <Spacer size={10} />
                <CarouselItemLabel>
                  <AnimateSlideText id={id + `-pin`} duration={0.6}>
                    <CarouselItemPin>
                      <Pin />
                    </CarouselItemPin>
                  </AnimateSlideText>
                  <Label>
                    <AnimateSplitText id={id + `-label`} delay={0.1}>
                      {label}
                    </AnimateSplitText>
                  </Label>
                </CarouselItemLabel>
              </>
            )}
          </CarouselItemText>
        </React.Fragment>
      </ConditionalWrapper>
    </CarouselItemMain>
  )
}

CarouselItem.propTypes = {
  id: PropTypes.string.isRequired,
  img: PropTypes.string,
  title: PropTypes.string,
  label: PropTypes.string,
  index: PropTypes.number,
  transitions: PropTypes.bool,
}

export default CarouselItem
